import { makeAutoObservable } from "mobx";

class MediaManager {
  // Singleton instance
  static instance = null;

  constructor() {
    if (MediaManager.instance) {
      return MediaManager.instance;
    }
    MediaManager.instance = this;

    /* MEDIA PLAYER */
    this.showingMediaPlayer = false;
    this.messageToView = null;
    this.serieToView = null;
    this.showingFullScreenVideo = false;
    this.videoStartTime = 0;
    this.videoMuted = true;

    makeAutoObservable(this);
  }

  // Singleton access method
  static getInstance() {
    if (!MediaManager.instance) {
      MediaManager.instance = new MediaManager();
    }
    return MediaManager.instance;
  }

  setProperty(propertyName, value) {
    if (propertyName in this) {
      this[propertyName] = value;
    } else {
      console.warn(
        `Property "${propertyName}" does not exist on MediaManager.`
      );
    }
  }

  // Legacy picker fallback (chat uses a persistent input rendered in ChatInputView).
  async loadImage() {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.style.display = "none";

      const cleanup = () => {
        input.onchange = null;
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      };

      input.onchange = () => {
        const file = input.files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            cleanup();
            resolve({
              uri: reader.result,
              file,
              cancelled: false,
            });
          };
          reader.onerror = (error) => {
            cleanup();
            reject(error);
          };
          reader.readAsDataURL(file);
        } else {
          cleanup();
          resolve({ cancelled: true });
        }
      };

      document.body.appendChild(input);
      input.click();
    });
  }

  async compressImageFileWeb(file, id, targetSizeBytes = 300 * 1024) {
    if (!file) {
      console.error("No image file provided");
      return null;
    }

    if (typeof file.type === "string" && !file.type.startsWith("image/")) {
      console.error("Unsupported file type:", file.type);
      return null;
    }

    try {
      let blob = file;
      const shouldConvertToJpeg = blob.type !== "image/jpeg";
      const shouldCompressForSize = blob.size > targetSizeBytes;

      if (shouldConvertToJpeg || shouldCompressForSize) {
        blob = await this.compressBlobWithCanvas(blob, targetSizeBytes);
      }

      const compressedFile = new File([blob], `${id}.jpg`, {
        type: "image/jpeg",
      });
      const uri = URL.createObjectURL(blob);

      return { uri, file: compressedFile };
    } catch (e) {
      console.error("compression failed:", e);
      return null;
    }
  }

  async compressMediaFileWeb(media, id, mediaType) {
    if (mediaType !== "image") {
      console.error("Only image compression is supported");
      return null;
    }

    if (media && typeof media.size === "number" && typeof media.type === "string") {
      return this.compressImageFileWeb(media, id);
    }

    if (typeof media === "string") {
      try {
        const resp = await fetch(media);
        const blob = await resp.blob();
        return this.compressImageFileWeb(blob, id);
      } catch (error) {
        console.error("compression failed:", error);
        return null;
      }
    }

    console.error("Unsupported media input for image compression");
    return null;
  }

  async compressBlobWithCanvas(blob, targetSize) {
    // load into an <img>
    const img = await new Promise((res, rej) => {
      const url = URL.createObjectURL(blob);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        res(image);
      };
      image.onerror = (error) => {
        URL.revokeObjectURL(url);
        rej(error);
      };
      image.src = url;
    });

    // paint on off‑screen canvas
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Unable to obtain canvas context for image compression");
    }
    ctx.drawImage(img, 0, 0);

    // estimate quality factor
    let quality = targetSize / blob.size;
    quality = Math.max(Math.min(quality, 1), 0.1);

    // export back out
    return await new Promise((res, rej) => {
      canvas.toBlob((newBlob) => {
        if (!newBlob) {
          rej(new Error("Canvas toBlob returned null"));
          return;
        }
        res(newBlob);
      }, "image/jpeg", quality);
    });
  }

  showMediaPlayer(message, serie) {
    if (serie) {
      this.setProperty("serieToView", serie);
      this.setProperty("messageToView", null);
      const client = serie?.exercise?.workout?.block?.client;
      const coach = client?.coach;
      if (client && coach) {
        coach.setProperty("selectedChatId", client.id);
        coach.setProperty("showingChatList", true);
      }
    }
    if (message) {
      this.setProperty("messageToView", message);
      this.setProperty("serieToView", null);
    }

    this.setProperty("showingMediaPlayer", true);
  }

  closeMediaPlayer() {
    this.setProperty("showingMediaPlayer", false);
    this.setProperty("messageToView", null);
    this.setProperty("serieToView", null);
    this.setProperty("showingFullScreenVideo", false);
    this.setProperty("videoStartTime", 0);
  }

  toggleFullScreenVideo() {
    this.setProperty("showingFullScreenVideo", !this.showingFullScreenVideo);
  }

  openFullScreenVideo(startTime = 0) {
    this.setProperty("videoStartTime", startTime);
    this.setProperty("showingFullScreenVideo", true);
  }

  closeFullScreenVideo() {
    this.setProperty("showingFullScreenVideo", false);
    this.setProperty("videoStartTime", 0);
  }

  setVideoMuted(muted) {
    this.setProperty("videoMuted", muted);
  }
}

export default MediaManager;
